import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PhoneOutlined, CalendarOutlined } from "@ant-design/icons";
import { useMessageApi } from "../../../../shared/providers/MessageProvider";
import { Loader } from "../../../../shared";
import { useZoomMeeting } from "../../hooks/useZoomMeeting";
import { getRateReviewLeadByToken } from "../../services/rateReviewApiService";
import { RATE_REVIEW_CONSTANTS } from "../../constants/rateReviewConstants";
import ZoomMeetingModal from "../Modals/ZoomMeetingModal/ZoomMeetingModal";
import logo from "../../../../assets/images/logo-colored.png";
import "./RateReviewStatusPage.scss";

/**
 * Rate Review Status Page Component
 */
const RateReviewStatusPage = () => {
  const { zohoId } = useParams();
  const navigate = useNavigate();
  const message = useMessageApi();

  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState(null);
  const [error, setError] = useState(null);
  const [shouldShowCompleted4, setShouldShowCompleted4] = useState(false);

  const zoomMeeting = useZoomMeeting(lead, zohoId, null);

  useEffect(() => {
    const fetchLead = async () => {
      if (!zohoId) {
        setError("Invalid link. Missing required parameters.");
        setLoading(false);
        return;
      }

      try {
        const response = await getRateReviewLeadByToken(zohoId);

        if (response.success) {
          setLead(response.data);
        } else {
          throw new Error(response.error || "Failed to load lead data");
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.error ||
          err.message ||
          "Failed to load rate review status. Please check your link.";
        setError(errorMessage);
        message.error(errorMessage, 3);
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [zohoId, message]);

  const getActiveStepIndexForEffect = () => {
    if (!lead) return -1;
    const currentStatus =
      lead.Lead_Status?.trim() || RATE_REVIEW_CONSTANTS.DEFAULT_LEAD_STATUS;
    const statusIndex = RATE_REVIEW_CONSTANTS.STATUS_STEPS.findIndex(
      (step) => step.value === currentStatus
    );
    return statusIndex >= 0 ? statusIndex : 0;
  };

  useEffect(() => {
    const activeStep = getActiveStepIndexForEffect();
    if (activeStep === 3) {
      const timer = setTimeout(() => {
        setShouldShowCompleted4(true);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setShouldShowCompleted4(false);
    }
  }, [lead]);

  if (loading) {
    return (
      <div className="rate-review-status-page">
        <Loader isLoading={true} text="Loading rate review status..." />
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="rate-review-status-page">
        <div className="error-container">
          <h2>Error Loading Status</h2>
          <p>
            {error ||
              "The rate review you're looking for doesn't exist or has been removed."}
          </p>
          <button className="btn-home" onClick={() => navigate("/")}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Get current status
  const currentStatus =
    lead.Lead_Status?.trim() || RATE_REVIEW_CONSTANTS.DEFAULT_LEAD_STATUS;
  const savingsRaw = lead.Savings || "";

  // Format savings to include "$" and "Monthly" if not already present
  const formatSavings = (savingsValue) => {
    if (!savingsValue) return "";
    let trimmed = savingsValue.trim();

    // Add dollar sign if not present
    if (!trimmed.startsWith("$")) {
      trimmed = `$${trimmed}`;
    }

    // Add "Monthly" if not present
    if (!trimmed.toLowerCase().includes("monthly")) {
      trimmed = `${trimmed} Monthly`;
    }

    return trimmed;
  };

  const savings = formatSavings(savingsRaw);

  // Determine which step is active
  const getActiveStepIndex = () => {
    const statusIndex = RATE_REVIEW_CONSTANTS.STATUS_STEPS.findIndex(
      (step) => step.value === currentStatus
    );
    return statusIndex >= 0 ? statusIndex : 0;
  };

  const activeStepIndex = getActiveStepIndex();

  // Get content based on status
  const getStatusContent = () => {
    switch (currentStatus) {
      case "Review Info Received":
        return {
          title: "Thank you for submitting your statements for a START review!",
          highlightText: "submitting",
          message: "",
          nextSteps:
            "A team member is being assigned to assist and will\nbe reaching out with further updates shortly.",
        };

      case "Review Being Analyzed":
        return {
          title: "We have an update on your START Rate Review!",
          message: "Great news, START has begun to analyze your statements.",
          nextSteps:
            "Our goal is to have your review completed within 24-48 hours.",
        };

      case "Calculating Savings":
        return {
          title: "We have an update on your START Rate Review!",
          message:
            "We have finished analyzing your statements and are currently getting your savings all added up. We will update you once we have the final savings for you to review.",
          nextSteps:
            "Once all your savings have been tallied START will send out another update for you to schedule a call to go over your savings.",
        };

      case "Schedule a Call to discuss":
        return {
          title: "START Saving Money Today",
          highlightText: "START",
          savings: savings,
          message: "",
          nextSteps:
            "You can schedule a call with our rep here to discuss our findings. We look forward to speaking with you.",
        };

      default:
        return {
          title: "Thank you for submitting your statements for a START review!",
          highlightText: "submitting",
          message: "",
          nextSteps:
            "A team member is being assigned to assist and will be reaching out with further updates shortly.",
        };
    }
  };

  const statusContent = getStatusContent();

  return (
    <div className="rate-review-status-page">
      {/* Header */}
      <header className="status-page-header">
        <div className="container">
          <div className="row align-items-center">
            {/* Left side: Logo and Title */}
            <div className="col-12 col-md-8">
              <div className="logo-title-wrapper">
                <a href="/" aria-label="Homepage">
                  <img src={logo} alt="START Merchant Services Logo" />
                </a>
                <span className="vertical-separator">|</span>
                <h1 className="page-title">
                  {currentStatus === "Schedule a Call to discuss"
                    ? "Schedule a Call"
                    : "Rate Review Received"}
                </h1>
              </div>
            </div>

            {/* Right side: Navigation */}
            <div className="col-12 col-md-4 text-end">
              <div className="header-nav">
                <a
                  href="https://startmerchantservices.com"
                  className="nav-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Home
                </a>
                <span className="nav-separator">|</span>
                <a
                  href="https://startmerchantservices.com/contact-us/"
                  className="nav-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - White Box */}
      <div className="content-wrapper">
        <section className="status-page-content">
          <div className="container">
            {/* Progress Tracker */}
            <div className="progress-tracker">
              <div className="outer_prog">
                <div
                  className={`${activeStepIndex === 1 ? "step2active" : ""} ${
                    activeStepIndex === 2 ? "step3active" : ""
                  } ${activeStepIndex === 3 ? "step3active" : ""} ${
                    activeStepIndex === 3 ? "step4on" : ""
                  }`}
                ></div>
                {RATE_REVIEW_CONSTANTS.STATUS_STEPS.map((step, index) => {
                  const isCompleted4 =
                    activeStepIndex === 3 &&
                    index === 3 &&
                    shouldShowCompleted4;
                  // When step 4 has completed4, remove active class and add complete class
                  const isActive = index === activeStepIndex && !isCompleted4;
                  const isCompleted = index < activeStepIndex;
                  // When third step (index 2) is active, first step (index 0) gets completed1 class
                  // When fourth step (index 3) is active, first step keeps completed1 class
                  const isCompleted1 =
                    (activeStepIndex === 2 && index === 0) ||
                    (activeStepIndex === 3 && index === 0);
                  // When fourth step (index 3) is active, second step (index 1) gets completed2 class
                  const isCompleted2 = activeStepIndex === 3 && index === 1;
                  // When fourth step (index 3) is active, third step (index 2) gets completed3 class
                  const isCompleted3 = activeStepIndex === 3 && index === 2;
                  // When step 4 (index 3) is active and step 2 (index 1) is complete, add arrow2 class
                  // Once applied, arrow2 class persists
                  const hasArrow2 =
                    activeStepIndex >= 3 && index === 1 && isCompleted;
                  // When step 3 (index 2) or later is active, first step (index 0) gets arrow1 class
                  // Once applied, arrow1 class persists (keeps showing even when step 4 becomes active)
                  const hasArrow1 =
                    activeStepIndex >= 2 && index === 0 && isCompleted;
                  // When step 4 has completed4 class, third step (index 2) gets arrow3 class
                  // Once applied, arrow3 class persists
                  const hasArrow3 =
                    shouldShowCompleted4 &&
                    activeStepIndex === 3 &&
                    index === 2;
                  // First step should always keep complete class when completed
                  // Step 4 should get complete class when it has completed4
                  const shouldHaveComplete =
                    isCompleted ||
                    (index === 0 && isCompleted1) ||
                    isCompleted4;

                  // Build className array to ensure all classes are applied
                  const classNames = [
                    "progress-step",
                    isActive && "active",
                    shouldHaveComplete && "complete",
                    isCompleted1 && "completed1",
                    isCompleted2 && "completed2",
                    isCompleted3 && "completed3",
                    isCompleted4 && "completed4",
                    hasArrow1 && "arrow1",
                    hasArrow2 && "arrow2",
                    hasArrow3 && "arrow3",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <div key={step.value} className={classNames}>
                      {step.label}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Main Message Section */}
            <div className="main-message-section">
              {statusContent.title && (
                <h2 className="main-title">
                  {statusContent.title.split(" ").map((word, index, arr) => {
                    if (
                      statusContent.highlightText &&
                      word.toUpperCase() ===
                        statusContent.highlightText.toUpperCase()
                    ) {
                      return (
                        <React.Fragment key={index}>
                          <span className="highlighted-word">{word}</span>
                          {index < arr.length - 1 && " "}
                        </React.Fragment>
                      );
                    }
                    return (
                      <React.Fragment key={index}>
                        <span className="title-rest">{word}</span>
                        {index < arr.length - 1 && " "}
                      </React.Fragment>
                    );
                  })}
                </h2>
              )}

              {/* Savings Display (for Schedule a Call status) */}
              {currentStatus === "Schedule a Call to discuss" && savings && (
                <div className="savings-display">
                  <p className="savings-label">
                    Save Up To:{" "}
                    <span className="savings-amount">{savings}</span>
                  </p>
                </div>
              )}

              {/* Status Message */}
              {statusContent.message && (
                <p className="status-message">{statusContent.message}</p>
              )}
            </div>

            {/* Next Steps Box */}
            <div className="next-steps-box">
              <div className="next-steps-header">
                <CalendarOutlined className="calendar-icon" />
                <h3 className="next-steps-title">Next Steps</h3>
              </div>
              <div className="next-steps-content">
                {currentStatus === "Schedule a Call to discuss" ? (
                  <>
                    {statusContent.nextSteps && (
                      <p className="next-steps-text">
                        {statusContent.nextSteps}
                      </p>
                    )}
                    <button
                      className="schedule-call-btn"
                      onClick={zoomMeeting.handleScheduleZoomMeeting}
                      disabled={zoomMeeting.loadingSchedulerUrl}
                    >
                      <PhoneOutlined />
                      <span>Schedule a Call</span>
                    </button>
                  </>
                ) : statusContent.nextSteps ? (
                  <p
                    className="next-steps-text"
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {statusContent.nextSteps}
                  </p>
                ) : (
                  <p className="next-steps-text">{statusContent.message}</p>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Zoom Meeting Modal */}
      <ZoomMeetingModal
        open={zoomMeeting.isZoomModalVisible}
        onCancel={zoomMeeting.handleZoomModalClose}
        schedulerUrl={zoomMeeting.schedulerUrl}
        slotError={zoomMeeting.slotError}
        lead={lead}
      />
    </div>
  );
};

export default RateReviewStatusPage;
